import { useMemo } from "react";
import { Editor } from "@tiptap/react";

import { Icon, SidebarMenu } from "utils/ui";
import { useAppDispatch } from "utils/store";
import { removeImageIfOrphaned } from "utils/api";
import { ImageBlockTypes } from "pages/tip-tap-editor/extensions/ImageBlock";

import { InsertPaletteMenu } from "../menus";
import { FieldTokenMenu } from "../menus/FieldTokenMenu/FieldTokenMenu";
import { SidebarImageBlockMenu } from "../menus/ImageBlockMenu";
import { SidebarHorizontalLineMenu } from "../menus/HorizontalLineBlockMenu";
import { SidebarSpacerBlockMenu } from "../menus/SpacerBlockMenu";
import { useTextmenuContentTypes } from "./hooks/useSidebarEditorState";

export const SideMenuControls = ({ editor }: { editor: Editor }) => {
  const dispatch = useAppDispatch();
  const { isImageBlock, isHorizontalLineBlock, isSpacerBlock, imageType, imageId } = useTextmenuContentTypes(editor);

  // Inspect mode when a configurable block is selected; otherwise Insert mode.
  const inspect = useMemo(() => {
    if (isImageBlock) return { title: "Image", body: <SidebarImageBlockMenu editor={editor} /> };
    if (isHorizontalLineBlock) return { title: "Divider", body: <SidebarHorizontalLineMenu editor={editor} /> };
    if (isSpacerBlock) return { title: "Spacer", body: <SidebarSpacerBlockMenu editor={editor} /> };
    return null;
  }, [editor, isImageBlock, isHorizontalLineBlock, isSpacerBlock]);

  // Back: collapse the node selection to a text cursor so we return to Insert.
  const onBack = () => {
    editor.chain().focus().setTextSelection(editor.state.selection.to).run();
  };

  const onDelete = () => {
    if (isImageBlock && imageType === ImageBlockTypes.IMAGE && imageId) {
      dispatch(removeImageIfOrphaned(imageId));
    }
    editor.chain().focus().deleteSelection().run();
  };

  return (
    <div
      className="bg-[var(--surface-a)] border-l border-[var(--surface-border)] shadow-[var(--dms-box-shadow)] z-[1] h-full flex flex-col"
      style={{ width: "300px" }}
    >
      {inspect ? (
        <>
          <div className="flex-none flex items-center gap-2.5 pt-3.5 px-4 pb-3 border-b border-[var(--surface-border)] bg-[var(--surface-b)]">
            <button
              type="button"
              className="flex-none w-7 h-7 flex items-center justify-center border-0 rounded-lg bg-transparent text-[var(--primary-600)] cursor-pointer hover:bg-[var(--surface-c)]"
              title="Back to insert"
              onClick={onBack}
            >
              <Icon name="ChevronLeft" size={17} strokeWidth={2} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold tracking-widest uppercase text-[var(--primary-500)] mb-0">
                Block settings
              </div>
              <div className="font-bold text-[var(--text-color)]">{inspect.title}</div>
            </div>
            <button
              type="button"
              className="flex-none w-7 h-7 flex items-center justify-center border-0 rounded-lg bg-transparent text-[var(--text-color-secondary)] cursor-pointer hover:bg-[var(--red-50)] hover:text-[var(--red-500)]"
              title="Delete block"
              onClick={onDelete}
            >
              <Icon name="Trash2" size={15} strokeWidth={1.9} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pt-3.5 px-4 pb-6 classy-scrollbar">{inspect.body}</div>
        </>
      ) : (
        <>
          <div className="flex-none pt-3.5 px-4 pb-3 border-b border-[var(--surface-border)]">
            <SidebarMenu.Heading title="Insert" />
            <SidebarMenu.Description>Click a block or field to add it at the cursor.</SidebarMenu.Description>
          </div>

          <div className="flex-1 overflow-y-auto pt-3.5 px-4 pb-6 classy-scrollbar">
            <InsertPaletteMenu editor={editor} />
            <FieldTokenMenu editor={editor} />
          </div>
        </>
      )}
    </div>
  );
};
